import * as React from "react";

import { Box } from "../../components/ui/box";
import { DrawerPinned } from "../../components/ui/drawer";
import { Button } from "../../components/ui/button";
import { Icon } from "../../components/ui/icon";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "../../components/ui/input-group";
import {
  Popover,
  PopoverPopup,
  PopoverTrigger,
  usePopoverSheet,
} from "../../components/ui/popover";
import { Separator } from "../../components/ui/separator";
import { Text } from "../../components/ui/text";
import {
  ThemeSwatchStrip,
  type ThemeSwatchColors,
} from "../../components/ui/theme-swatch";
import type { Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";

// The search well and the header controls hold the 28px popup-chrome rung, the
// same one VisualPicker's search line holds. The rungs step up on touch by
// default, which is right for a form field and wrong for a popup's own chrome.
const ROW_CONTROL = "h-7 min-w-7";

/** One row: what to paint, what to call it, what it is worth. */
export interface ThemeCatalogEntry {
  key: string;
  title: string;
  colors: ThemeSwatchColors;
  /** The theme's own hairline, when it names one. */
  border?: string;
  /** Rows are grouped under their section's heading, in the order given. */
  section?: string;
}

export type ThemeCatalogPickerProps = Omittable<{
  entries: readonly ThemeCatalogEntry[];
  value: string;
  onValueChange: (key: string) => void;
  /** What opens the popup. Rendered as-is, so the host owns its anatomy. */
  trigger: React.ReactElement;
  /** Caps the scrolling list. The search field stays put above it. */
  maxListHeight?: number;
}>;

/**
 * The theme picker: a search field over the palettes, grouped by section.
 *
 * ONE axis — the palette. A palette is a light+dark pair, so the appearance
 * axis would double every row; the host owns that control and hands this one
 * the half it wants previewed, already resolved. That is why an entry carries
 * literal `colors` rather than a theme name: the picker never resolves a
 * palette and never reads a token, so a row can show a light theme while the
 * document is dark.
 *
 * A list this long has to be searchable, and the field owns the first line —
 * where the hand goes first, and where `initialFocus` puts the caret. The
 * filtered list is its own feedback, so there is no count beside it.
 */
export function ThemeCatalogPicker({
  entries,
  value,
  onValueChange,
  trigger,
  maxListHeight = 320,
}: ThemeCatalogPickerProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const searchRef = React.useRef<HTMLInputElement | null>(null);

  // The search resets per opening: a stale query would hide the list the next
  // time the popup comes up, with no visible cause.
  React.useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const filtered = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return entries;
    return entries.filter((e) => e.title.toLowerCase().includes(query));
  }, [entries, search]);

  // Sections in first-seen order, so the host's array IS the running order and
  // there is no second place to keep them sorted.
  const sheet = usePopoverSheet();
  const sections = React.useMemo(() => {
    const out = new Map<string, ThemeCatalogEntry[]>();
    for (const entry of filtered) {
      const name = entry.section ?? "";
      const bucket = out.get(name);
      if (bucket) bucket.push(entry);
      else out.set(name, [entry]);
    }
    return [...out];
  }, [filtered]);

  return (
    // Closes on a pick, like the select this trigger is deliberately
    // indistinguishable from. Browsing with it open reads well on a standalone
    // page, but this popup overlays a settings panel and a compact account
    // menu, and leaving it up covers the rest of the decision.
    // `responsive`: a bottom sheet on a phone — the search takes the keyboard.
    <Popover onOpenChange={setOpen} open={open} responsive>
      <PopoverTrigger
        data-slot="theme-catalog-picker-trigger"
        render={trigger}
      />
      <PopoverPopup
        align="start"
        className="w-72"
        initialFocus={searchRef}
        padding="none"
        sheetTitle="Theme"
        side="bottom"
      >
        {/* In the sheet the search pins above the scroller with the caption. */}
        <DrawerPinned>
        <Box direction="column" gap="sm" padding="sm">
          {/* The framed search well, matching the selector-popover grammar:
              full width on its own first line, the popup-chrome rung, and a
              magnifier — this list is long enough that the field has to read as
              a search rather than a nameless box. */}
          <InputGroup
            className={cn(ROW_CONTROL, "w-full **:[input]:h-full")}
            variant="outline"
          >
            <InputGroupAddon>
              <Icon name="search" size="sm" />
            </InputGroupAddon>
            <InputGroupInput
              aria-label="Search themes"
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search themes..."
              ref={searchRef}
              value={search}
            />
          </InputGroup>
        </Box>
        </DrawerPinned>

        <Separator />

        {/* In the sheet the panel is the one scroller: no cap of its own. */}
        <Box
          direction="column"
          maxHeight={sheet ? undefined : maxListHeight}
          overflowY={sheet ? undefined : "auto"}
          padding="sm"
        >
          {sections.length === 0 ? (
            <Box paddingX="xs" paddingY="sm">
              <Text size="sm" variant="faint">
                No themes match "{search}"
              </Text>
            </Box>
          ) : (
            sections.map(([name, rows]) => (
              <Box direction="column" key={name}>
                {name ? (
                  <Box
                    data-slot="theme-catalog-section"
                    paddingBottom="2xs"
                    paddingTop="xs"
                    paddingX="xs"
                  >
                    <Text size="xs" variant="faint" weight="medium">
                      {name}
                    </Text>
                  </Box>
                ) : null}
                {rows.map((entry) => (
                  <Button
                    aria-pressed={entry.key === value}
                    className={cn("justify-start gap-2", "h-8 px-2")}
                    key={entry.key}
                    onClick={() => {
                      onValueChange(entry.key);
                      setOpen(false);
                    }}
                    shape="square"
                    size="sm"
                    // Option rows are content: the theme name rests at full ink.
                    ink="foreground"
                    variant="ghost"
                  >
                    <ThemeSwatchStrip
                      border={entry.border}
                      colors={entry.colors}
                    />
                    <Text
                      className="min-w-0 flex-1 truncate text-left"
                      size={null}
                    >
                      {entry.title}
                    </Text>
                    {entry.key === value ? (
                      <Icon name="check" size="sm" />
                    ) : null}
                  </Button>
                ))}
              </Box>
            ))
          )}
        </Box>
      </PopoverPopup>
    </Popover>
  );
}
